// "Your policy says" — the figure a register page runs to, and where it comes from: the
// organisation's adopted policy (with the sentence it was read from), a setting made by hand, or the
// platform default. Server rules in backend/src/lib/operatingParameters.ts; contract in
// docs/OPERATING-PARAMETERS.md. Display only: the registers apply the figure on the server.

import { useEffect, useState } from "react";
import { FileText, SlidersHorizontal } from "lucide-react";
import { api, getViewingOrgId } from "../api/client";
import { PortalLink } from "./PortalLink";
import { theme } from "../theme";

export interface OperatingParameterView {
  key: string;
  label: string;
  group: string;
  groupLabel: string;
  /** "score" is a risk score on the register's 1-25 scale (risk appetite); "percent" a whole percentage; "count" a number of records. */
  unit: "hours" | "days" | "months" | "years" | "score" | "percent" | "count";
  value: number;
  configured: number;
  source: "default" | "manual" | "policy";
  capped: boolean;
  defaultValue: number;
  defaultBasis: string;
  min: number;
  max: number;
  options: number[] | null;
  tighter: "lower" | "higher";
  limit: { value: number; basis: string } | null;
  appliedBy: string[];
  href: string | null;
  decisions: Array<{ templateId: string; decision: string; templateTitle: string | null }>;
  stored: { setAt: string | null; quote: string | null; fileName: string | null; documentVersion: number | null; policyTemplateId: string | null; policyTitle: string | null } | null;
}

export interface OperatingParametersResponse {
  parameters: OperatingParameterView[];
  proposals: Array<{ id: string; parameterKey: string; value: number; quote: string; status: "pending" | "refused"; limit: { value: number; basis: string } | null; policyTemplateId: string; policyTitle: string; documentVersion: number; createdAt: string }>;
  extractions: Array<{ id: string; policyTemplateId: string; policyTitle: string; fileName: string; documentVersion: number; status: string; error: string | null; found: number; createdAt: string; completedAt: string | null }>;
  drift: Array<{ id: string; type: string; title: string; description: string; severity: "high" | "medium" | "low"; parameterKey: string | null }>;
  context: { ceEnrolled: boolean; pciEnrolled: boolean };
  canEdit: boolean;
  aiAvailable: boolean;
}

export function formatParameterValue(value: number, unit: OperatingParameterView["unit"]): string {
  if (unit === "score") return `score ${value}`;
  if (unit === "percent") return `${value}%`;
  if (unit === "count") return String(value);
  const singular = unit.slice(0, -1);
  return `${value} ${value === 1 ? singular : unit}`;
}

// One request per page view and organisation, shared by every chip on the page.
let cache: { org: string | null; at: number; promise: Promise<OperatingParametersResponse> } | null = null;

export function loadOperatingParameters(force = false): Promise<OperatingParametersResponse> {
  const org = getViewingOrgId();
  if (!force && cache && cache.org === org && Date.now() - cache.at < 60_000) return cache.promise;
  const promise = api.get<OperatingParametersResponse>("/operating-parameters");
  cache = { org, at: Date.now(), promise };
  promise.catch(() => { cache = null; });
  return promise;
}

export function PolicySaysChip({ keys }: { keys: string[] }) {
  const [params, setParams] = useState<OperatingParameterView[] | null>(null);
  const joined = keys.join(",");

  useEffect(() => {
    let live = true;
    loadOperatingParameters()
      .then((r) => { if (live) setParams(r.parameters.filter((p) => joined.split(",").includes(p.key))); })
      .catch(() => { if (live) setParams([]); });
    return () => { live = false; };
  }, [joined]);

  if (!params?.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {params.map((p) => <Chip key={p.key} p={p} />)}
    </div>
  );
}

function Chip({ p }: { p: OperatingParameterView }) {
  const fromPolicy = p.source === "policy";
  const lead = fromPolicy ? "Your policy says" : p.source === "manual" ? "Set by your organisation" : "Platform default";
  const tooltip = [
    `${p.label}: ${formatParameterValue(p.value, p.unit)}`,
    fromPolicy && p.stored?.quote ? `"${p.stored.quote}" (${p.stored.policyTitle ?? "adopted policy"}${p.stored.documentVersion ? ` v${p.stored.documentVersion}` : ""})` : null,
    p.capped && p.limit ? `Shortened to the limit: ${p.limit.basis}` : null,
    "Open Operating parameters to change it",
  ].filter(Boolean).join("\n");
  return (
    <PortalLink
      to="/operating-parameters"
      title={tooltip}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 8px", borderRadius: 2,
        border: `1px solid ${fromPolicy ? theme.color.ref : theme.color.border}`,
        background: fromPolicy ? theme.color.accentBg : theme.color.panel,
        color: theme.color.text, fontSize: 11, textDecoration: "none", lineHeight: 1.6,
      }}
    >
      {fromPolicy ? <FileText size={11} color={theme.color.ref} /> : <SlidersHorizontal size={11} color={theme.color.subtle} />}
      <span style={{ color: theme.color.subtle }}>{lead}</span>
      <span style={{ fontWeight: 600 }}>{p.label.toLowerCase()}: {formatParameterValue(p.value, p.unit)}</span>
    </PortalLink>
  );
}
